import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import NovelCard from '@/components/feature/NovelCard';
import { categories, heroRecommended, hotRanking, novels } from '@/mocks/novels';

const faqItems = [
  {
    question: '可爱小说推荐网站都收录哪些类型的小说？',
    answer: '我们精心收录甜宠小说、萌系小说、治愈系小说、校园小说和奇幻小说等多种类型。每一本推荐都经过编辑团队的认真筛选，确保让你读到最暖心、最治愈的作品。从霸道总裁到青梅竹马，从魔法世界到校园日常，总有一本能打动你的心。',
  },
  {
    question: '如何找到适合自己口味的可爱小说？',
    answer: '你可以通过首页的分类导航快速浏览不同风格的小说。甜宠小说适合喜欢甜蜜恋爱故事的读者；萌系小说以可爱角色和温馨日常为主；治愈系小说用温柔的文字抚慰心灵；校园小说带你重回青春时代；奇幻小说则在奇妙的世界观中带来温暖与感动。',
  },
  {
    question: '网站推荐的小说多久更新一次？',
    answer: '我们每周都会更新推荐书单，持续追踪最新的优质可爱小说。同时，我们也会定期推出主题合集，比如"春日治愈书单"、"夏日甜宠推荐"等，让你不错过任何一本好小说。',
  },
  {
    question: '可爱小说和普通言情小说有什么区别？',
    answer: '可爱小说更注重轻松温暖的阅读体验。相比传统言情小说，可爱小说的角色设定更加软萌可爱，故事情节更加温馨治愈，文字风格更加清新甜美。它们像一杯热可可，在疲惫的时候给予你最温柔的陪伴，没有虐心桥段，只有满满的甜蜜与治愈。',
  },
  {
    question: '可以推荐几本适合新读者的入门可爱小说吗？',
    answer: '当然可以！如果你是第一次接触可爱小说，我们强烈推荐《猫系少女的甜蜜日常》——萌系日常的代表作；《总裁的棉花糖恋人》——甜宠天花板；还有《小狐狸的治愈时光》——治愈系中的经典。这三本风格各异，可以帮你快速找到自己喜欢的类型。',
  },
];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '可爱小说推荐',
  url: 'https://keaixiaoshuo.com',
  description: '精选可爱小说推荐，涵盖甜宠、萌系、治愈系等多种风格，让你沉浸在暖心故事中。',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://keaixiaoshuo.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '可爱小说推荐',
  url: 'https://keaixiaoshuo.com',
  description: '最萌治愈系小说推荐平台',
  sameAs: ['https://weibo.com/keaixiaoshuo', 'https://www.xiaohongshu.com/keaixiaoshuo'],
};

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <SEO
        title="可爱小说推荐 - 最萌治愈系小说合集2024 | 甜宠·萌系·治愈系"
        description="精选可爱小说推荐，涵盖甜宠、萌系、治愈系等多种风格，让你沉浸在暖心故事中。无论是可爱少女日常还是甜蜜爱情，这里有最全可爱小说书单，陪你度过每一个温柔时光。"
        keywords="可爱小说,可爱小说推荐,萌系小说,甜宠小说,治愈系小说,好看的可爱小说,甜文小说"
        canonicalUrl="https://keaixiaoshuo.com"
      />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Soft%20pastel%20illustration%20of%20a%20cozy%20reading%20nook%20with%20books%2C%20warm%20candlelight%2C%20plush%20pillows%2C%20floating%20sparkles%2C%20dreamy%20pink%20and%20cream%20color%20palette%2C%20kawaii%20aesthetic%2C%20whimsical%20atmosphere%2C%20soft%20bokeh%20effect&width=1600&height=900&seq=hero-bg-home&orientation=landscape"
            alt="可爱小说推荐 - 最萌治愈系小说合集"
            title="可爱小说推荐 - 最萌治愈系小说合集"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4 w-full">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-4 tracking-tight">
              可爱小说推荐
            </h1>
            <p className="text-base md:text-xl text-white/90 max-w-2xl leading-relaxed">
              精选最萌治愈系小说合集，涵盖甜宠、萌系、治愈系等多种风格<br className="hidden sm:block" />
              让你沉浸在暖心故事中，陪你度过每一个温柔时光
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <Link
                to="/category/tianchong"
                className="px-6 py-2.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                探索甜宠小说
              </Link>
              <Link
                to="/category/mengxi"
                className="px-6 py-2.5 rounded-full bg-white/20 backdrop-blur-sm text-white font-medium text-sm border border-white/30 hover:bg-white/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览萌系小说
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                探索小说分类
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                找到最适合你的那一本可爱小说
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex flex-col items-center gap-3 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200/60 hover:bg-primary-50/50 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-secondary-100 group-hover:bg-primary-100 transition-colors duration-300">
                    <i className={`${cat.icon} text-xl md:text-2xl text-secondary-600 group-hover:text-primary-500 transition-colors duration-300`}></i>
                  </div>
                  <span className="text-sm md:text-base font-medium text-foreground-800 group-hover:text-primary-500 transition-colors duration-300">
                    {cat.name}
                  </span>
                  <p className="text-xs text-foreground-500 text-center leading-relaxed hidden sm:block">
                    {cat.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hero Recommended */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                精选推荐
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                编辑团队精心甄选，最值得一读的可爱小说
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {heroRecommended.map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Ranking */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                热门书单
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                读者最爱的可爱小说排行榜
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotRanking.map((novel, index) => (
                <div key={novel.id} className="relative">
                  {index < 3 && (
                    <div className="absolute -top-2 -left-2 z-10 w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-accent-500 text-white text-xs font-bold">
                      {index + 1}
                    </div>
                  )}
                  <NovelCard novel={novel} />
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/tianchong"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                查看更多推荐
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Feature Highlights */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-accent-100/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                为什么选择我们
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                用心推荐每一本可爱小说
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { icon: 'ri-bookmark-line', title: '精选书单', desc: '每本小说都经过编辑团队认真筛选，确保内容优质、风格可爱。' },
                { icon: 'ri-heart-pulse-line', title: '多元风格', desc: '甜宠、萌系、治愈系等多种风格全覆盖，满足不同读者的喜好。' },
                { icon: 'ri-refresh-line', title: '每周更新', desc: '持续追踪最新优质可爱小说，书单每周更新，好书不错过。' },
                { icon: 'ri-user-heart-line', title: '温暖社区', desc: '和志同道合的读者一起分享阅读感受，发现更多宝藏小说。' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center p-5 rounded-lg bg-background-50 border border-background-200/70">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 mb-3">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                最新推荐
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                刚刚加入书单的可爱小说
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {novels.slice(8, 12).map((novel) => (
                <NovelCard key={novel.id} novel={novel} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading text-foreground-950">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-500 mt-2">
                关于可爱小说推荐的常见问题解答
              </p>
            </div>
            <div className="flex flex-col gap-3 md:gap-4">
              {faqItems.map((item, i) => (
                <details key={i} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm md:text-base font-medium text-foreground-900 list-none">
                    {item.question}
                    <i className="ri-arrow-down-s-line text-lg text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cozy%20reading%20corner%20with%20soft%20blankets%20and%20warm%20lighting%2C%20stack%20of%20books%2C%20pastel%20pink%20and%20cream%20aesthetic%2C%20dreamy%20atmosphere%2C%20soft%20focus&width=1600&height=600&seq=cta-bg-home&orientation=landscape"
            alt="开始阅读可爱小说"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/35"></div>
          <div className="relative z-10 max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-4xl font-heading text-white mb-3">
              准备好开始你的阅读之旅了吗？
            </h2>
            <p className="text-sm md:text-base text-white/85 mb-6 leading-relaxed">
              从甜宠到治愈，从校园到奇幻 —— 找到属于你的那一本可爱小说，让故事温暖你的每一天。
            </p>
            <Link
              to="/category/tianchong"
              className="inline-block px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              开始探索可爱小说
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}